import React, { useEffect, useMemo, useState } from "react";
import MyVenuesPanel from "./MyVenuesPanel";
import PendingPosts from "./PendingPosts";
import Loader from "../icons/Loader";
import { useMediaQuery } from "../../util/useMediaQuery";
import { useUser } from "../../context/UserContext/UserProvider";
import { Link } from "react-router-dom";
import ClaimedVenues from "./claimedVenues";
import barMarkerSelected from "../../assets/map/bar-marker-selected.png";
import { PortalTitle } from "../../pages/Portal";
import Button from "../Button/Button";
import { modal } from "../../util/useModal";
import { usePortalContext } from "../../context/PortalContext/portalcontext";

const VENUE_STATUS_TICKET_STATUSES = new Set([
  "pending",
  "awaiting review",
  "awaiting_review",
  "rejected",
  "approved",
]);

const getTicketVenue = (ticket) =>
  ticket?.type === "claim" ? ticket?.existing_resource : ticket?.resource;

const getResourceId = (resource) =>
  typeof resource === "string" ? resource : resource?._id || resource?.id;

const getTicketVenueId = (ticket) =>
  getResourceId(getTicketVenue(ticket)) ||
  getResourceId(ticket?.resource) ||
  getResourceId(ticket?.existing_resource) ||
  getResourceId(ticket?.venue) ||
  getResourceId(ticket?.venue_id) ||
  getResourceId(ticket?.resource_id);

const normalizeTicketStatus = (status) =>
  (status || "pending").toString().trim().toLowerCase();

const getVenueStatusTickets = (tickets = [], userVenues = []) => {
  const claimedVenueIds = new Set(
    userVenues.map((venue) => getResourceId(venue)).filter(Boolean)
  );

  return tickets.filter((ticket) => {
    const status = normalizeTicketStatus(ticket?.status);
    if (!VENUE_STATUS_TICKET_STATUSES.has(status)) return false;

    const venueId = getTicketVenueId(ticket);
    return !venueId || !claimedVenueIds.has(venueId);
  });
};

const VenuesDashboard = ({ userLocation, targetVenuePortalTab, setTargetVenuePortalTab }) => {
  const {user} = useUser();
  const {
    setCurrentVenuePortalTab,
    venueDashboardData,
    fetchVenueDashboardData,
    setVenueDashboardTickets,
    refreshVenueDashboardVenues,
    refreshVenueDashboardUserVenues,
  } = usePortalContext();
  const medium = useMediaQuery("md");
  const [currentTab, setCurrentTab] = useState("new_venue");
  const [selectedVenue, setSelectedVenue] = useState({});
  const {
    tickets,
    userVenues,
    venues,
    loading,
    loaded,
  } = venueDashboardData || {};

  useEffect(() => {
    if (!targetVenuePortalTab) return;
    setCurrentTab(targetVenuePortalTab);
    setCurrentVenuePortalTab?.(targetVenuePortalTab);
    setTargetVenuePortalTab?.(null);
  }, [targetVenuePortalTab, setCurrentVenuePortalTab, setTargetVenuePortalTab]);

  useEffect(() => {
    setCurrentVenuePortalTab?.(currentTab);
  }, [currentTab, setCurrentVenuePortalTab]);

  const refreshAll = (arg) => {
    return fetchVenueDashboardData({ force: true, location: arg || userLocation });
  };

  const refreshUserVenues = async () => {
    return refreshVenueDashboardUserVenues();
  };

  const refreshVenues = async (marker) => {
    return refreshVenueDashboardVenues(marker || userLocation);
  };

  useEffect(() => {
    fetchVenueDashboardData();
  }, [fetchVenueDashboardData]);

  if (!user?.emailVerified) {
    return (
      <div className="p-kit-32  justify-center px-kit-48 text-kit-lg text-center flex items-center pt-kit-64 bg-[var(--inyc-primary)] min-h-[calc(100vh-25rem)] font-bold h-[60px] w-full duration-300">
        <div>
          before you can post we need to verify your email.
          <br />
          <br />
          verify by clicking{" "}
          <Link className="inline-block font-['Primary-Medium'] ml-kit-6" to="/auth/verify-email">
            here
          </Link>
        </div>
      </div>
    );
  }

  const getSelectedVenueState = (vn) => ({
    venue: { ...vn, me: userVenues?.some((v) => v._id === vn._id) },
    marker: {
      icon: barMarkerSelected,
      lat: vn.location.coordinates[1],
      lng: vn.location.coordinates[0],
    },
  });

  const handleVenuPick = async (vn,showPrompt = false) => {
    //if venue is different from selected venue, then notify user that they're switching venues
    console.log('selectedVenue venuesDashboard',selectedVenue)
    console.log('vn',vn)
    if (selectedVenue.venue?._id !== vn._id && showPrompt) {
      const proceed = await modal(({ proceed }) => (
        <div className="text-center p-kit-32">
          <div className="text-kit-md mb-kit-32 text-[var(--inyc-primary-text-color)]">
            you're about to switch venues. do you want to select the {vn.name} venue, or would you like to just use it's address
          </div>
          <div className="flex gap-kit-16 justify-center">
            <Button onClick={() => proceed(false)}>only use address</Button>
            <Button onClick={() => proceed(true)}>switch venues</Button>
          </div>
        </div>
      ));

      if (!proceed) return;
    }

    setSelectedVenue(getSelectedVenueState(vn));
    setCurrentTab("new_venue");

    // add tags to input
    // setInputTags(vn.tags);

    // mapRef.current.flyTo([
    //   vn.location.coordinates[1],
    //   vn.location.coordinates[0],
    // ]);
  };

  const statusTickets = useMemo(
    () => getVenueStatusTickets(tickets, userVenues),
    [tickets, userVenues]
  );

  const handleStatusTicketsChange = (updater) => {
    setVenueDashboardTickets((currentTickets = []) => {
      const nextStatusTickets =
        typeof updater === "function" ? updater(statusTickets || []) : updater;
      const nextStatusIds = new Set(
        (nextStatusTickets || []).map((ticket) => ticket?._id).filter(Boolean)
      );

      return currentTickets.filter((ticket) => {
        const wasVisibleInStatus = statusTickets?.some(
          (statusTicket) => statusTicket?._id === ticket?._id
        );
        return !wasVisibleInStatus || nextStatusIds.has(ticket?._id);
      });
    });
  };

  const refreshControl = (
    <div className="flex justify-end px-kit-16">
      <button
        type="button"
        className="rounded-kit-full border-[1px] border-[var(--inyc-outline-color)] bg-[var(--inyc-secondary-text-color)] px-kit-12 py-kit-4 text-kit-xs font-['Primary-Medium'] lowercase text-[var(--inyc-primary-text-color)] transition-all duration-200 hover:opacity-75 disabled:cursor-default disabled:opacity-50"
        disabled={loading}
        onClick={() => fetchVenueDashboardData({ force: true })}
      >
        {loading ? "refreshing" : "refresh"}
      </button>
    </div>
  );

  return (
    <div style={{ ...styles.Container, ...(!medium ? styles.ContainerMobile : {}) }}>
      {loading && !loaded ? (
        <div
          style={{
            width: "100%",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Loader />
        </div>
      ) : (
        <div>
          <PortalTitle>
            venues dashboard
          </PortalTitle>
          {currentTab === "new_venue" && (
            <MyVenuesPanel
              userLocation={userLocation}
              userVenues={userVenues}
              venues={venues}
              refreshAll={refreshAll}
              refreshUserVenues={refreshUserVenues}
              refreshVenues={refreshVenues}
              selectedVenue={selectedVenue}
              handleVenuPick={handleVenuPick}
              setSelectedVenue={setSelectedVenue}
            />
          )}
          {currentTab === "pending_venues" && (
            <PendingPosts
              tickets={statusTickets}
              onTicketsChange={handleStatusTicketsChange}
              refreshControl={refreshControl}
            />
          )}
          {currentTab === "claimed_venues" &&  <ClaimedVenues 
            userVenues={userVenues}
            userLocation={userLocation}
            venues={venues}
            refreshAll={refreshAll}
            refreshVenues={refreshVenues}
            selectedVenue={selectedVenue}
            setSelectedVenue={setSelectedVenue}
            handleVenuPick={(v) => setSelectedVenue(getSelectedVenueState(v))}
            refreshControl={refreshControl}
          />}
          </div>
      )}
    </div>
  );
};

const styles = {
  Container: {
    padding: "0 var(--inyc-space-20) var(--inyc-space-20)",
    width: "100%",
    height: "100%",
    backgroundColor: 'var(--inyc-primary)',
  },
  ContainerMobile: {
    padding: 0,
  },
};

export default VenuesDashboard;
